import React from 'react';
import { Box, ButtonBase, Checkbox, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { tok, LABEL, MONO, fmtPct, fmtNum } from '../results/ui';

// Backtests as a comparison list (DWLF-306; canvas board BacktestList). Each row's numbers are the
// API's summary for that run (rowHeadline), loaded lazily; tick completed runs to compare them.

const STATUS = {
  completed: { label: 'Completed', tone: 'up' }, processing: { label: 'Running', tone: 'accent' },
  pending: { label: 'Queued', tone: 'warn' }, failed: { label: 'Failed', tone: 'down' },
};
const statusOf = (s) => STATUS[s] || { label: s || '—', tone: 'ink-55' };
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) : '—');
const COLS = '40px minmax(0, 2.4fr) 120px 140px 92px 92px 72px 64px 64px 48px';
export const MAX_COMPARE = 3;

function Metric({ value, tone = 'text' }) {
  return <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.8125rem', textAlign: 'right', color: tok(tone) }}>{value}</Typography>;
}

// Phone width (DWLF-307): each run as a card — name, status, then the five numbers in a row.
function RowCard({ bt, h, isSel, canSelect, onToggle, onView, onDelete }) {
  const st = statusOf(bt.status);
  const loading = bt.status === 'completed' && h === undefined;
  const dash = loading ? '…' : '—';
  const cells = [
    ['Return', h ? fmtPct(h.returnPct, 1) : dash, h?.returnPct == null ? 'ink-55' : h.returnPct >= 0 ? 'up' : 'down'],
    ['Max DD', h?.maxDrawdownPct != null ? `−${Math.abs(h.maxDrawdownPct).toFixed(1)}%` : dash, h?.maxDrawdownPct != null ? 'down' : 'ink-55'],
    ['Win', h?.winRatePct != null ? `${h.winRatePct.toFixed(0)}%` : dash, 'text'],
    ['PF', h ? fmtNum(h.profitFactor) : dash, 'text'],
    ['Trades', h?.trades != null ? String(h.trades) : dash, 'text'],
  ];
  return (
    <Box sx={{ p: 1.5, borderBottom: `1px solid ${tok('ink-06')}`, borderLeft: `2px solid ${isSel ? tok('accent') : 'transparent'}`, backgroundColor: isSel ? tok('accent-04') : 'transparent' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Checkbox size="small" checked={isSel} disabled={!canSelect} onChange={() => onToggle(bt.requestId)} sx={{ p: 0.5 }}
          inputProps={{ 'aria-label': `Compare ${bt.strategyName}` }} />
        <ButtonBase onClick={() => onView(bt.requestId)} sx={{ flex: 1, minWidth: 0, display: 'block', textAlign: 'left' }}>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('text'), lineHeight: 1.4 }}>{bt.strategyName || bt.strategyId}</Typography>
          <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('ink-50') }}>
            <Box component="span" sx={{ ...LABEL, fontSize: '0.5625rem', color: tok(st.tone), mr: 1 }}>{st.label}</Box>
            {fmtDay(bt.startDate)} → {fmtDay(bt.endDate)} · {(bt.symbols || []).join(' · ')}
          </Typography>
        </ButtonBase>
        <IconButton size="small" aria-label={`Delete ${bt.strategyName}`} onClick={() => onDelete(bt)} sx={{ color: tok('ink-50') }}><DeleteOutline fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 1, mt: 1, pl: 4.5 }}>
        {cells.map(([k, v, tone]) => (
          <Box key={k}>
            <Typography sx={{ ...LABEL, fontSize: '0.5rem', color: tok('ink-50') }}>{k}</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.75rem', color: tok(tone) }}>{v}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function BacktestRows({ backtests, headlines, selected, onToggle, onView, onDelete }) {
  const full = selected.length >= MAX_COMPARE;
  const narrow = useMediaQuery('(max-width: 899px)');
  if (narrow) {
    return (
      <Box role="list" aria-label="Backtests" sx={{ border: `1px solid ${tok('ink-08')}`, backgroundColor: tok('surface') }}>
        {backtests.map((bt) => {
          const isSel = selected.includes(bt.requestId);
          return (
            <Box role="listitem" key={bt.requestId}>
              <RowCard bt={bt} h={headlines[bt.requestId]} isSel={isSel} canSelect={bt.status === 'completed' && (isSel || !full)}
                onToggle={onToggle} onView={onView} onDelete={onDelete} />
            </Box>
          );
        })}
      </Box>
    );
  }
  return (
    <Box role="table" aria-label="Backtests" sx={{ border: `1px solid ${tok('ink-08')}`, backgroundColor: tok('surface'), overflowX: 'auto' }}>
      <Box role="row" sx={{ display: 'grid', gridTemplateColumns: COLS, gap: 1.5, alignItems: 'center', px: 2, minHeight: 40, minWidth: 980, borderBottom: `1px solid ${tok('ink-08')}` }}>
        {['', 'Strategy · symbols', 'Status', 'Period', 'Return', 'Max DD', 'Win', 'PF', 'Trades', ''].map((h, i) => (
          <Typography key={i} component="span" role="columnheader" sx={{ ...LABEL, fontSize: '0.5625rem', color: tok('ink-50'), textAlign: i >= 4 && i <= 8 ? 'right' : 'left' }}>{h}</Typography>
        ))}
      </Box>
      {backtests.map((bt) => {
        const st = statusOf(bt.status);
        const h = headlines[bt.requestId];
        const isSel = selected.includes(bt.requestId);
        const canSelect = bt.status === 'completed' && (isSel || !full);
        const loading = bt.status === 'completed' && h === undefined;
        const dash = loading ? '…' : '—';
        return (
          <Box role="row" key={bt.requestId}
            sx={{ display: 'grid', gridTemplateColumns: COLS, gap: 1.5, alignItems: 'center', px: 2, minHeight: 56, minWidth: 980,
              borderBottom: `1px solid ${tok('ink-06')}`, backgroundColor: isSel ? tok('accent-04') : 'transparent', borderLeft: `2px solid ${isSel ? tok('accent') : 'transparent'}` }}>
            <Checkbox size="small" checked={isSel} disabled={!canSelect} onChange={() => onToggle(bt.requestId)}
              inputProps={{ 'aria-label': bt.status !== 'completed' ? `${bt.strategyName}: only completed runs can be compared` : `Compare ${bt.strategyName}` }} />
            <ButtonBase onClick={() => onView(bt.requestId)} sx={{ justifyContent: 'flex-start', textAlign: 'left', display: 'block', minWidth: 0 }}>
              <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('text') }}>{bt.strategyName || bt.strategyId}</Typography>
              <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('ink-50') }}>{(bt.symbols || []).join(' · ')}</Typography>
            </ButtonBase>
            <Typography component="span" sx={{ ...LABEL, fontSize: '0.5625rem', color: tok(st.tone) }}>{st.label}</Typography>
            <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.75rem', color: tok('ink-60') }}>{fmtDay(bt.startDate)} → {fmtDay(bt.endDate)}</Typography>
            <Metric value={h ? fmtPct(h.returnPct, 1) : dash} tone={h?.returnPct == null ? 'ink-55' : h.returnPct >= 0 ? 'up' : 'down'} />
            <Metric value={h?.maxDrawdownPct != null ? `−${Math.abs(h.maxDrawdownPct).toFixed(1)}%` : dash} tone={h?.maxDrawdownPct != null ? 'down' : 'ink-55'} />
            <Metric value={h?.winRatePct != null ? `${h.winRatePct.toFixed(0)}%` : dash} />
            <Metric value={h ? fmtNum(h.profitFactor) : dash} />
            <Metric value={h?.trades != null ? String(h.trades) : dash} />
            <Tooltip title="Delete backtest">
              <IconButton size="small" aria-label={`Delete ${bt.strategyName}`} onClick={() => onDelete(bt)} sx={{ color: tok('ink-50'), '&:hover': { color: tok('down') } }}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
}
